import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getSlackJobs, getSlackQueue, type SlackJobRow, type SlackQueueStats } from "@admin/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@admin/components/ui/card";
import { formatDate } from "@admin/lib/utils";

function DepthCard({ label, value }: { label: string; value: number }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-fg">{label}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-semibold tabular-nums">{value}</div>
      </CardContent>
    </Card>
  );
}

export default function SlackQueuePage() {
  const [stats, setStats] = useState<SlackQueueStats | null>(null);
  const [jobs, setJobs] = useState<SlackJobRow[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    Promise.all([getSlackQueue(), getSlackJobs(20)])
      .then(([queueStats, recentJobs]) => {
        setStats(queueStats);
        setJobs(recentJobs);
      })
      .catch(() => setErr("Could not load Slack queue stats"));
  }, []);

  if (err) return <p className="text-danger">{err}</p>;
  if (!stats || !jobs) return <p className="text-muted-fg">Loading...</p>;

  const backlog = stats.users.filter((u) => u.pending > 0 || u.processing > 0);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Slack message queue</h1>
        <p className="mt-1 text-sm text-muted-fg">
          Durable inbound Slack jobs on the control plane (MySQL). Drained every 2 minutes and on VM
          bridge reconnect.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
        <DepthCard label="Pending" value={stats.totals.pending} />
        <DepthCard label="Processing" value={stats.totals.processing} />
        <DepthCard label="Done (retained)" value={stats.totals.done} />
        <DepthCard label="Cancelled" value={stats.totals.cancelled} />
        <DepthCard label="Dead (30d+)" value={stats.totals.dead} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Per user / VM backlog</CardTitle>
        </CardHeader>
        <CardContent>
          {backlog.length === 0 ? (
            <p className="text-sm text-muted-fg">No pending or processing jobs.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-muted-fg">
                    <th className="pb-2 pr-4 font-medium">User</th>
                    <th className="pb-2 pr-4 font-medium">VM status</th>
                    <th className="pb-2 pr-4 font-medium">Bridge live</th>
                    <th className="pb-2 pr-4 font-medium">Pending</th>
                    <th className="pb-2 pr-4 font-medium">Processing</th>
                    <th className="pb-2 font-medium">Oldest pending</th>
                  </tr>
                </thead>
                <tbody>
                  {backlog.map((row) => (
                    <tr key={row.userId} className="border-b border-border/60">
                      <td className="py-2 pr-4">
                        <Link
                          to={`/users/${row.userId}`}
                          className="text-accent hover:underline"
                        >
                          {row.email}
                        </Link>
                      </td>
                      <td className="py-2 pr-4 tabular-nums">{row.serverStatus ?? "-"}</td>
                      <td className="py-2 pr-4">{row.vmConnected ? "yes" : "no"}</td>
                      <td className="py-2 pr-4 tabular-nums">{row.pending}</td>
                      <td className="py-2 pr-4 tabular-nums">{row.processing}</td>
                      <td className="py-2 tabular-nums">
                        {row.oldestPendingAt ? formatDate(row.oldestPendingAt) : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Recent jobs (debug)</CardTitle>
        </CardHeader>
        <CardContent>
          {jobs.length === 0 ? (
            <p className="text-sm text-muted-fg">No slack_message_jobs rows yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-muted-fg">
                    <th className="pb-2 pr-4 font-medium">Created</th>
                    <th className="pb-2 pr-4 font-medium">Status</th>
                    <th className="pb-2 pr-4 font-medium">Inbound</th>
                    <th className="pb-2 font-medium">Reply</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job) => (
                    <tr key={job.id} className="border-b border-border/60 align-top">
                      <td className="py-2 pr-4 tabular-nums whitespace-nowrap">
                        {formatDate(job.createdAt)}
                      </td>
                      <td className="py-2 pr-4 tabular-nums">{job.status}</td>
                      <td className="py-2 pr-4 max-w-xs">
                        <span className="font-mono text-xs text-muted-fg">{job.id.slice(0, 8)}</span>
                        <p className="mt-1 break-words">{job.textPrefix}</p>
                      </td>
                      <td className="py-2 max-w-md break-words">
                        {job.replyPrefix ?? <span className="text-muted-fg">-</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
